import React, { Fragment, Component } from 'react';
import PropTypes from 'prop-types';
import AvatarEditor from 'react-avatar-editor';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faTimes } from '@fortawesome/pro-light-svg-icons/faTimes';
import { faPencil } from '@fortawesome/pro-light-svg-icons/faPencil';
import { faExpand } from '@fortawesome/pro-light-svg-icons/faExpand';
import classnames from 'classnames';
import CircleShadow from './Buttons/CircleShadow';
import { fillImageWithColor, guessImageBackgroundColor } from '../../utils/guessImageColor';

const EditorStates = {
  Normal: 'normal',
  Editing: 'editing',
};

class ImageUpload extends Component {
  _isMounted = false;

  state = {
    selectedImage: null,
    editorState: EditorStates.Normal,
    imgScale: 1,
    averageColor: '#FFFFFF',
    errorImage: false,
    proportion: 1,
  };

  componentWillUnmount() {
    this._isMounted = false;
  }

  handleImageChange = (event) => {
    const avatarMaxSize = 4194304;
    const { onError, t } = this.props;
    if (!event.target.files) {
      this.setState({ selectedImage: null, editorState: EditorStates.Normal });
      return;
    }
    const file = event.target.files[0];

    if (!file.type) {
      return;
    }

    const pattern = /image\/jpeg|image\/png/;
    if (!file.type.match(pattern)) {
      onError(t('errorInvalidFileExtension'));
      return;
    }

    if (file.size >= avatarMaxSize) {
      onError(t('errorInvalidFileSize'));
      return;
    }

    this.setState({ selectedImage: file, editorState: EditorStates.Editing, imgScale: 1 });
  };

  handleConfirmImage = async () => {
    this._isMounted = true;
    const { onSelectImage, backgroundEnabled } = this.props;
    const { averageColor } = this.state;
    const image = backgroundEnabled ? await fillImageWithColor(this.avatarEditor.getImage().toDataURL(), averageColor) : this.avatarEditor.getImage().toDataURL();
    await onSelectImage(image);
    if (this._isMounted) this.setState({ editorState: EditorStates.Normal });
    if (this.fileElement) this.fileElement.value = '';
  };

  cancelEdition = () => {
    this.setState({ selectedImage: null, editorState: EditorStates.Normal, imgScale: 1, averageColor: '#FFFFFF' });
    if (this.fileElement) this.fileElement.value = '';
  };

  resizeImage = () => {
    let { imgScale } = this.state;
    imgScale += 0.5;
    if (imgScale > 3.5) imgScale = 1;
    this.setState({ imgScale });
  };

  onLoadImage = async (e) => {
    const { width, height } = this.props;
    const [imageWidth, imageHeight] = [e.width, e.height];
    const [scaleWidth, scaleHeight] = [width / imageWidth, height / imageHeight];
    const realProportion = scaleWidth < scaleHeight ? scaleWidth : scaleHeight;

    const averageColor = await guessImageBackgroundColor(e.resource);
    this.setState({ averageColor, proportion: realProportion });
  };

  render() {
    const { selectedImage, editorState, errorImage, imgScale, averageColor, proportion } = this.state;
    const { image, isLoading, letter, style, width, height, radius, className, backgroundEnabled } = this.props;
    const classes = classnames(`image-upload ${className}`, { editing: editorState === EditorStates.Editing, errorImage });

    const rootStyle = averageColor && backgroundEnabled ? { background: averageColor } : {};

    if (isLoading) {
      return (
        <Fragment>
          <div style={{ ...rootStyle, ...style }} className={classes}>
            {errorImage ? <div className="letter">{letter}</div> : null}
            <div className="loading lds-ellipsis">
              <div />
              <div />
              <div />
              <div />
            </div>
          </div>
        </Fragment>
      );
    }
    return (
      <Fragment>
        <div style={{ ...rootStyle, ...style }} className={classes}>
          {errorImage && <div className="letter">{letter}</div>}
          {editorState === EditorStates.Editing ? (
            <Fragment>
              <AvatarEditor
                ref={(ref) => {
                  this.avatarEditor = ref;
                }}
                image={selectedImage}
                border={1}
                borderRadius={radius}
                color={[255, 255, 255, 1]} // RGBA
                scale={imgScale * proportion}
                width={width}
                height={height}
                rotate={0}
                disableHiDPIScaling
                onLoadSuccess={this.onLoadImage}
              />
              <CircleShadow className="confirm" darkMode onClick={this.handleConfirmImage}>
                <FontAwesomeIcon icon={faCheck} />
              </CircleShadow>
              <CircleShadow className="cancel" darkMode onClick={this.cancelEdition}>
                <FontAwesomeIcon icon={faTimes} />
              </CircleShadow>
              <CircleShadow className="resize" darkMode onClick={this.resizeImage}>
                <FontAwesomeIcon icon={faExpand} />
              </CircleShadow>
            </Fragment>
          ) : (
            <Fragment>
              <input
                style={{ display: 'none' }}
                type="file"
                ref={(ref) => {
                  this.fileElement = ref;
                }}
                onChange={this.handleImageChange}
              />
              <img
                src={`${image}`}
                alt=""
                onError={() => this.setState({ errorImage: true })}
                onLoad={() => this.setState({ errorImage: false })}
                style={{ display: errorImage ? 'none' : '' }}
              />
              <CircleShadow className="upload" onClick={() => this.fileElement.click()}>
                <FontAwesomeIcon icon={faPencil} />
              </CircleShadow>
            </Fragment>
          )}
        </div>
      </Fragment>
    );
  }
}

ImageUpload.defaultProps = {
  t: (t) => t,
  width: 150,
  height: 150,
  radius: 75,
  letter: 'L',
  backgroundEnabled: true,
};

ImageUpload.propTypes = {
  image: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  width: PropTypes.number,
  height: PropTypes.number,
  radius: PropTypes.number,
  t: PropTypes.func,
  letter: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  onError: PropTypes.func.isRequired,
  onSelectImage: PropTypes.func.isRequired,
  backgroundEnabled: PropTypes.bool,
};

export default ImageUpload;
